import { useEffect, useRef, useState } from "react";

import {
  apiClient,
  type DashboardMatrix,
  type DashboardMatrixPoint,
  type SocialReportDashboard,
  type SnapshotFacetFilters
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { BubbleLegend, BubbleMatrix, tonesByKey } from "@/shared/components/charts/BubbleMatrix";
import { ChartDownload, ChartToggle } from "@/shared/components/charts/chart-kit";
import { Panel } from "@/shared/components/charts/Panel";
import { MultiSelectFilter } from "@/shared/components/MultiSelectFilter";
import { formatInteger } from "@/shared/utils/format";

import { DASHBOARD_MATRIX_GROUPS, type DashboardMatrixGroup } from "@/shared/vocabulary/dashboard-matrix";

import type { SourceVisibility } from "./dashboard-v1";

export type ReportReference = { kind: "report" | "draft"; id: number };

const GROUP_LABELS: Record<DashboardMatrixGroup, string> = {
  all: "Tudo",
  brand: "Marca",
  spokesperson: "Porta-voz",
  theme_group: "Tema",
  theme: "Subtema",
  period: "Períodos"
};

const FILTERABLE: DashboardMatrixGroup[] = ["spokesperson", "theme_group", "theme"];

/** Os pontos de uma matriz em CSV (`;`, vírgula decimal, BOM — o dialeto
 *  Excel pt-BR de `dashboard-csv.ts`). */
export function matrixCsv(points: DashboardMatrixPoint[]): string {
  const number = (value: number | null) => (value === null ? "" : String(value).replace(".", ","));
  const quote = (value: string) => (/[";\n]/.test(value) ? `"${value.replaceAll("\"", "\"\"")}"` : value);
  const header = "Grupo;Publicações;Relevância do veículo;Relevância do conteúdo;CENA média";
  return [header, ...points.map(point => [quote(point.label), point.count, number(point.x), number(point.y), number(point.average_cena)].join(";"))].join("\r\n");
}

function downloadCsv(points: DashboardMatrixPoint[], name: string) {
  const url = URL.createObjectURL(new Blob([`﻿${matrixCsv(points)}`], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${name}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function optionsFor(data: SocialReportDashboard, group: DashboardMatrixGroup): Array<{ value: string; label: string }> {
  const source = group === "spokesperson" ? data.spokespersons ?? [] : group === "theme_group" ? data.theme_groups : group === "theme" ? data.themes : [];
  return source.filter(item => item.key !== "__none__").map(item => ({ value: item.key, label: item.key }));
}

/**
 * As "Matrizes Estratégicas" do v1: imprensa e redes, lado a lado, com o
 * agrupamento das bolhas, o conteúdo dentro delas e a legenda lateral.
 * A matriz tem rota própria (`/dashboard/matrix`): trocar o agrupamento
 * não pode refazer o dashboard inteiro.
 */
export function StrategicMatrixPanel({ token, reference, filters, data, showsCena, sources }: {
  token: string;
  reference: ReportReference;
  filters: SnapshotFacetFilters;
  data: SocialReportDashboard;
  showsCena: boolean;
  /** A matriz de uma fonte fora da tela some, e a outra ocupa a largura. */
  sources: SourceVisibility;
}) {
  const [group, setGroup] = useState<DashboardMatrixGroup>("brand");
  const [items, setItems] = useState<string[]>([]);
  const [draftItems, setDraftItems] = useState<Set<string>>(new Set());
  const [matrix, setMatrix] = useState<DashboardMatrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLabel, setShowLabel] = useState(true);
  const [showCount, setShowCount] = useState(true);
  const [showCena, setShowCena] = useState(false);
  const [legendCount, setLegendCount] = useState(true);
  const [legendCena, setLegendCena] = useState(false);
  const loadId = useRef(0);
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    const id = ++loadId.current;
    setLoading(true);
    setError(null);
    apiClient
      .socialReportDashboardMatrix(
        token,
        reference.kind === "report" ? { reportId: reference.id } : { draftId: reference.id },
        JSON.parse(filtersKey) as SnapshotFacetFilters,
        group,
        FILTERABLE.includes(group) ? items : []
      )
      .then(result => { if (id === loadId.current) setMatrix(result); })
      .catch(cause => { if (id === loadId.current) setError(errorMessage(cause, "Não foi possível carregar as matrizes.")); })
      .finally(() => { if (id === loadId.current) setLoading(false); });
  }, [token, reference.kind, reference.id, filtersKey, group, items]);

  const chooseGroup = (next: DashboardMatrixGroup) => {
    setGroup(next);
    setItems([]);
    setDraftItems(new Set());
  };
  const options = optionsFor(data, group);
  // Uma ordem de cores comum às duas matrizes: pelo volume somado das
  // duas fontes (período: pela ordem do calendário).
  const totals = new Map<string, number>();
  for (const point of [...(matrix?.press ?? []), ...(matrix?.network ?? [])]) totals.set(point.key, (totals.get(point.key) ?? 0) + point.count);
  const toneOf = tonesByKey(
    [...totals.entries()].sort((a, b) => (group === "period" ? a[0].localeCompare(b[0]) : b[1] - a[1] || a[0].localeCompare(b[0]))).map(([key]) => key)
  );
  const cards = [
    { key: "press" as const, title: "Matriz de Imprensa" },
    { key: "network" as const, title: "Matriz de Redes" }
  ].filter(card => sources[card.key]);

  return (
    <Panel
      title="Matrizes Estratégicas"
      subtitle={`${cards.length > 1 ? "Imprensa e redes" : cards[0].key === "press" ? "Imprensa" : "Redes"} por relevância do veículo e relevância do conteúdo — a bolha é a média do grupo`}
      className="dashboard-span-12"
    >
      <div className="matrix-controls">
        <div className="matrix-control-block">
          <span className="matrix-control-title">Visualizar bolhas por</span>
          <ChartToggle
            label="Agrupamento das bolhas"
            value={group}
            options={DASHBOARD_MATRIX_GROUPS.map(value => ({ value, label: GROUP_LABELS[value] }))}
            onChange={chooseGroup}
          />
        </div>
        <div className="matrix-control-block">
          <span className="matrix-control-title">Dentro das bolhas</span>
          <div className="matrix-checks">
            <label><input type="checkbox" checked={showLabel} onChange={event => setShowLabel(event.target.checked)}/>Texto</label>
            <label><input type="checkbox" checked={showCount} onChange={event => setShowCount(event.target.checked)}/>Qtd.</label>
            {showsCena && <label><input type="checkbox" checked={showCena} onChange={event => setShowCena(event.target.checked)}/>CENA</label>}
          </div>
        </div>
        <div className="matrix-control-block">
          <span className="matrix-control-title">Legenda lateral</span>
          <div className="matrix-checks">
            <label><input type="checkbox" checked={legendCount} onChange={event => setLegendCount(event.target.checked)}/>Qtd.</label>
            {showsCena && <label><input type="checkbox" checked={legendCena} onChange={event => setLegendCena(event.target.checked)}/>CENA</label>}
          </div>
        </div>
      </div>
      {FILTERABLE.includes(group) && (
        <div className="matrix-items">
          <MultiSelectFilter
            closeOnOutsideClick
            label={`${GROUP_LABELS[group]}s para comparar`}
            options={options}
            selected={draftItems}
            exclude={false}
            onExcludeChange={() => undefined}
            onToggle={value => setDraftItems(current => {
              const next = new Set(current);
              if (next.has(value)) next.delete(value);
              else next.add(value);
              return next;
            })}
            onClear={() => setDraftItems(new Set())}
          />
          <button type="button" className="secondary-button" onClick={() => setItems([...draftItems])} disabled={[...draftItems].join("\n") === items.join("\n")}>
            Aplicar
          </button>
          <span className="matrix-items-meta">{items.length ? `${items.length} escolhido(s)` : "As 16 mais volumosas"}</span>
        </div>
      )}
      {error && <p className="dashboard-inline-error" role="alert">{error}</p>}
      <div className={`matrix-pair${cards.length > 1 ? "" : " matrix-pair--single"}`} aria-busy={loading}>
        {cards.map(source => {
          const points = matrix?.[source.key] ?? [];
          const total = points.reduce((sum, point) => sum + point.count, 0);
          return (
            <section key={source.key} className="matrix-card" data-export-root>
              <header className="matrix-card-head">
                <div>
                  <h3 className="matrix-card-title">{source.title}</h3>
                  <span>{matrix ? `${formatInteger(total)} menções · ${points.length} ${points.length === 1 ? "bolha" : "bolhas"}` : loading ? "Carregando…" : ""}</span>
                </div>
                <span className="dashboard-panel-actions">
                  <ChartDownload name={source.title}/>
                  <button type="button" className="chart-download-button" disabled={!points.length} onClick={() => downloadCsv(points, source.title)}>CSV</button>
                </span>
              </header>
              <div className="matrix-card-body">
                <div>
                  {matrix && (
                    <BubbleMatrix
                      points={points}
                      title={source.title}
                      showLabel={showLabel}
                      showCount={showCount}
                      showCena={showsCena && showCena}
                      ringOnly={group === "period"}
                      toneOf={toneOf}
                    />
                  )}
                </div>
                <BubbleLegend points={points} showCount={legendCount} showCena={showsCena && legendCena} toneOf={toneOf}/>
              </div>
            </section>
          );
        })}
      </div>
    </Panel>
  );
}
